import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  formatBalance,
  formatLastActive,
  plotDisplay,
  memberRoleLabel,
  roleBadgeVariant,
  statusLabel,
} from '@/lib/member-display'
import type { MemberListItem } from '@riverpark/types'
import { cn } from '@/lib/utils'

interface MembersTableProps {
  members: MemberListItem[]
  basePath: string
  isLoading?: boolean
}

function RoleBadge({ member }: { member: MemberListItem }) {
  return (
    <Badge variant={roleBadgeVariant(member.role)}>
      {memberRoleLabel(member.role, member.plots)}
    </Badge>
  )
}

function StatusCell({ status }: { status: MemberListItem['status'] }) {
  const dotClass =
    status === 'active'
      ? 'bg-status-paid-text'
      : status === 'invited'
        ? 'bg-[hsl(var(--invited-bg))]'
        : 'bg-muted-foreground'

  return (
    <div className="flex items-center gap-2 font-sans text-sm text-ink">
      <span className={cn('size-2 rounded-full', dotClass)} aria-hidden />
      {statusLabel(status)}
    </div>
  )
}

export function MembersTable({ members, basePath, isLoading }: MembersTableProps) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] border-collapse font-sans text-sm">
          <thead>
            <tr className="border-b border-border-2 bg-cream-2/50">
              <th className="px-6 py-3 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plot</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Role</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Balance</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last active</th>
              <th className="px-6 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-border-2">
                  <td colSpan={7} className="px-6 py-4">
                    <div className="h-10 animate-pulse rounded bg-cream-2" />
                  </td>
                </tr>
              ))}

            {!isLoading && members.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                  No members match your search.
                </td>
              </tr>
            )}

            {!isLoading &&
              members.map((member) => {
                const balance = formatBalance(member.outstandingBalance)
                const primaryPlot = member.plots[0]

                return (
                  <tr
                    key={member.id}
                    className="border-b border-border-2 transition-colors last:border-b-0 hover:bg-cream-2/30"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-ink">{member.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {member.email ?? member.phone}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {primaryPlot ? (
                        <>
                          <div className="font-medium text-ink">{primaryPlot.newNumber}</div>
                          {member.plots.length === 1 && (
                            <div className="text-xs text-muted-foreground">
                              ({primaryPlot.oldNumber})
                            </div>
                          )}
                          {member.plots.length > 1 && (
                            <div className="text-xs text-muted-foreground">
                              +{member.plots.length - 1} more
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">{plotDisplay(member.plots)}</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <RoleBadge member={member} />
                    </td>
                    <td className="px-4 py-4">
                      <StatusCell status={member.status} />
                    </td>
                    <td className={cn('px-4 py-4 font-sans text-sm', balance.className)}>
                      {balance.label}
                    </td>
                    <td className="px-4 py-4 text-muted-foreground">
                      {formatLastActive(member.lastLoginAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`${basePath}/${member.id}`}>
                        <Button variant="secondary" size="sm" type="button">
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
